import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Building2, Link as LinkIcon, MoveRight, Pencil, Plus, Trash } from 'lucide-react';

import AutopilotSourceLogo from 'common/common/AutopilotSourceLogo';
import Linkify from 'common/common/Linkify';
import Portal from 'common/common/Portal';
import UserAvatarV2 from 'common/common/UserAvatarV2';
import { InsightCreatedVia } from 'common/constants/insights';
import { SourceTypes } from 'common/constants/sources';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import ErrorMessage from 'common/ErrorMessage';
import useBackgroundClick from 'common/hooks/useBackgroundClick';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import SpinnerV2 from 'common/SpinnerV2';
import CreateIdeaFromInsightModal from 'common/subdomain/admin/v2/AdminV2IdeaSheet/InternalView/Insights/CreateIdeaFromInsightModal';
import InsightEditor from 'common/subdomain/admin/v2/AdminV2IdeaSheet/InternalView/Insights/InsightEditor';
import {
  InsightQueriesContext,
  type InsightQueriesState,
  type InsightQueryState,
  getQueryKey,
} from 'common/subdomain/admin/v2/containers/InsightListContainer';
import {
  useDeleteInsight,
  useMoveInsight,
} from 'common/subdomain/admin/v2/hooks/useInsightMutations';
import IdeaSearch from 'common/subdomain/admin/v2/ideas/IdeaSearch';
import Timestamp from 'common/Timestamp';
import DropdownMenu from 'common/ui/DropdownMenu';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import VStack from 'common/ui/VStack';
import Button from 'common/uiV3/Button';
import Spinner from 'common/uiV3/Spinner';

import CannyLogo from 'img/canny-icon.svg';

import type { Idea } from 'common/api/endpoints/idea';
import type { Insight } from 'common/api/endpoints/insight';
import type { View } from 'common/api/endpoints/view';

type InsightProps = {
  insight: Insight;
};

const InsightCompany = ({ insight: { company } }: InsightProps) => {
  if (!company) {
    return null;
  }

  return (
    <HStack className="button-link items-center gap-1">
      <Building2 size={16} />
      <Link to={`/admin/users/company/${company.urlName}`} className="text-sm">
        {company.name}
      </Link>
    </HStack>
  );
};

const InsightUsers = ({ insight: { users } }: InsightProps) => {
  if (!users?.length) {
    return null;
  }

  const user = users[0];
  return (
    <HStack className="button-link items-center gap-1">
      <UserAvatarV2 user={user} size={16} />
      <Link to={`/admin/users/${user.urlName}`} className="text-sm">
        {user.name}
      </Link>
    </HStack>
  );
};

const InsightCreated = ({ insight }: InsightProps) => {
  return (
    <HStack className="items-center gap-1 text-sm">
      <Timestamp timestamp={insight.created} />
    </HStack>
  );
};

const InsightAuthor = ({ insight }: InsightProps) => {
  const { author, source } = insight;

  let authorLabel: string | undefined;
  if (source.type === SourceTypes.Autopilot) {
    authorLabel = 'Autopilot';
  } else if (author) {
    authorLabel = author.name;
  }

  if (authorLabel) {
    return <span className="text-sm capitalize">{authorLabel}</span>;
  }

  return null;
};

const InsightSource = ({ insight }: InsightProps) => {
  const { source, url } = insight;
  if (!url) {
    return null;
  }

  const PortalEntities = [
    InsightCreatedVia.post,
    InsightCreatedVia.vote,
    InsightCreatedVia.comment,
  ];
  const isViaPortal = insight.createdVia && PortalEntities.includes(insight.createdVia);

  let logo = null;
  if (source.type === SourceTypes.Autopilot) {
    logo = <AutopilotSourceLogo className="size-4" integrationName={source.name} size="small" />;
  } else if (source.type === SourceTypes.Manual && isViaPortal) {
    logo = <LazyLoadedImage src={CannyLogo} className="size-4" alt="" />;
  } else {
    logo = <LinkIcon size={16} />;
  }

  return (
    <HStack className="items-center gap-1">
      {logo}
      <a className="underline cursor-pointer" href={url} target="_blank" rel="noopener noreferrer">
        <span className="text-sm">View Source</span>
      </a>
    </HStack>
  );
};

type InsightManageProps = {
  insight: Insight;
  onEdit: (insight: Insight) => void;
  onError: (error: string | null) => void;
  reloadInsights: (blockLoader?: boolean) => void;
  selectedView?: View | null;
};

const InsightManage = ({
  insight,
  onEdit,
  onError,
  reloadInsights,
  selectedView,
}: InsightManageProps) => {
  const showToast = useContext(ShowToastContext);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<Portal>(null);

  const [moving, setMoving] = useState(false);
  const [creatingIdeaFromInsight, setCreatingIdeaFromInsight] = useState(false);

  const moveInsight = useMoveInsight({
    onError: (error) => onError(error.message),
    onSuccess: () => {
      reloadInsights(true);
      showToast('Insight moved', ToastTypes.success);
    },
  });

  const deleteInsight = useDeleteInsight({
    onError: (error) => onError(error.message),
    onSuccess: () => {
      reloadInsights(true);
      showToast('Insight deleted', ToastTypes.success);
    },
  });

  useBackgroundClick(() => {
    setMoving(false);
  }, [portalRef, buttonRef]);

  const onDelete = async () =>
    deleteInsight.mutate({
      insightID: insight._id,
    });

  const onMove = async (idea: Idea) =>
    moveInsight.mutate({
      insightID: insight._id,
      ideaID: idea._id,
    });

  const loading = deleteInsight.isLoading || moveInsight.isLoading;

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger>
          <button
            className="unstyled-button button-link underline text-sm"
            disabled={loading}
            ref={buttonRef}>
            {loading ? <SpinnerV2 size="small" /> : 'Manage'}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" side="bottom">
          <DropdownMenu.Item icon={Pencil} label="Edit Insight" onSelect={() => onEdit(insight)} />
          <DropdownMenu.Item
            icon={MoveRight}
            label="Move Insight"
            onSelect={() => setMoving(true)}
          />
          <DropdownMenu.Item
            icon={Plus}
            label="Create Idea"
            onSelect={() => setCreatingIdeaFromInsight(true)}
          />
          <DropdownMenu.Item
            icon={Trash}
            label="Delete Insight"
            variant="destructive"
            onSelect={onDelete}
          />
        </DropdownMenu.Content>
      </DropdownMenu>
      {moving && (
        <Portal align="start" position="bottom" ref={portalRef} relativeToRef={buttonRef}>
          <IdeaSearch onSelectIdea={onMove} />
        </Portal>
      )}
      {creatingIdeaFromInsight && (
        <CreateIdeaFromInsightModal
          onClose={() => setCreatingIdeaFromInsight(false)}
          onSuccess={() => reloadInsights(true)}
          insight={insight}
          selectedView={selectedView}
        />
      )}
    </>
  );
};

type InsightItemProps = {
  insight: Insight;
  onEdit: (insight: Insight) => void;
  reloadInsights: (blockLoader?: boolean) => void;
  selectedView?: View | null;
};

const InsightItem = ({ insight, onEdit, reloadInsights, selectedView }: InsightItemProps) => {
  const [error, setError] = useState<string | null>(null);

  const detailsLeft = [
    ['users', InsightUsers({ insight })],
    ['company', InsightCompany({ insight })],
    ['created', InsightCreated({ insight })],
    ['manage', InsightManage({ insight, onEdit, onError: setError, reloadInsights, selectedView })],
  ]
    .filter(([_, element]) => Boolean(element))
    .flatMap(([key, element]) => [
      <React.Fragment key={`${key}`}>{element}</React.Fragment>,
      <Flex className="items-center" key={`middot-${key}`}>
        &middot;
      </Flex>,
    ])
    .slice(0, -1);

  const detailsRight = [
    ['author', InsightAuthor({ insight })],
    ['source', InsightSource({ insight })],
  ]
    .filter(([_, element]) => Boolean(element))
    .flatMap(([key, element]) => [
      <React.Fragment key={`${key}`}>{element}</React.Fragment>,
      <Flex className="items-center" key={`middot-${key}`}>
        &middot;
      </Flex>,
    ])
    .slice(0, -1);

  return (
    <VStack className="gap-3 base-border-l border-l-2 pl-3">
      {insight.value ? (
        <p>
          <Linkify>{insight.value}</Linkify>
        </p>
      ) : null}
      <Flex className="gap-2 text-gray-110 justify-between">
        <Flex className="gap-1 max-w-[70%] flex-wrap">{detailsLeft}</Flex>
        <Flex className="shrink-0 gap-1">{detailsRight}</Flex>
      </Flex>
      {error ? <ErrorMessage size="small">{error}</ErrorMessage> : null}
    </VStack>
  );
};

type ConnectProps = {
  insightQuery?: InsightQueryState;
};
type OwnProps = {
  idea: Idea;
  selectedView?: View | null;
};

type Props = ConnectProps & OwnProps;

function InsightList({ insightQuery, idea, selectedView }: Props) {
  const { loadQuery: loadInsightQuery, invalidateInsightQueries } =
    useContext(InsightQueriesContext);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editingInsight, setEditingInsight] = useState<Insight | null>(null);
  const [loaderBlocked, setLoaderBlocked] = useState(false);

  useEffect(() => {
    if (!idea) {
      return;
    }

    loadInsightQuery({
      ideaID: idea?._id,
    });
  }, [loadInsightQuery, idea]);

  const reloadInsights = useCallback(
    async (blockLoader: boolean = false) => {
      if (blockLoader) {
        setLoaderBlocked(true);
      }

      await invalidateInsightQueries();
      await loadInsightQuery({
        ideaID: idea?._id,
      });

      setLoaderBlocked(false);
    },
    [invalidateInsightQueries, loadInsightQuery, idea?._id],
  );

  const { items: insights, loading } = insightQuery || { items: [], loading: true };

  return (
    <VStack className="gap-6 px-5 py-3">
      <Button onClick={() => setCreatorOpen(true)} size="small">
        Add insight
      </Button>
      {creatorOpen && (
        <InsightEditor
          idea={idea}
          onClose={() => setCreatorOpen(false)}
          onSuccess={() => reloadInsights(true)}
        />
      )}
      {loading && !loaderBlocked && (
        <HStack className="gap-2 items-center">
          <Spinner size="large" />
          <p>Loading insights, please wait...</p>
        </HStack>
      )}
      {insights?.map((insight: Insight) => {
        const editing = editingInsight?._id === insight._id;
        if (editing) {
          return (
            <InsightEditor
              key={insight._id}
              idea={idea}
              insight={insight}
              onClose={() => setEditingInsight(null)}
              onSuccess={() => reloadInsights(true)}
            />
          );
        }
        return (
          <InsightItem
            key={insight._id}
            insight={insight}
            onEdit={setEditingInsight}
            reloadInsights={reloadInsights}
            selectedView={selectedView}
          />
        );
      })}
    </VStack>
  );
}

export default connect(
  ({ insightQueries }: { insightQueries: InsightQueriesState }, { idea }: OwnProps) => {
    const queryKey = getQueryKey({ ideaID: idea?._id });
    const insightQuery = insightQueries[queryKey];
    return { insightQuery };
  },
)(InsightList) as unknown as React.FC<OwnProps>;
